import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MapPin, Mail, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT, NAV_LINKS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Watch your inbox.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-zinc-800/80 bg-[#050507]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <Logo />
            <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
              A UK creative engineering agency building bespoke websites, mobile apps —
              and AI Warehouse, our own marketing platform that promotes AI products
              and services.
            </p>
            <form
              onSubmit={subscribe}
              className="flex items-center max-w-sm bg-black border border-zinc-800 rounded-full p-1.5 focus-within:border-cyan-400 transition-colors"
              data-testid="footer-newsletter-form"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for sharp insights"
                data-testid="footer-newsletter-input"
                className="flex-1 min-w-0 bg-transparent px-4 py-2 text-sm placeholder:text-zinc-600 focus:outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="shrink-0 rounded-full bg-cyan-400 text-black px-5 py-2.5 font-semibold text-sm hover:bg-white transition-colors disabled:opacity-60"
              >
                Join
              </button>
            </form>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
              Sitemap
            </h3>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-${link.testId}`}
                    className="text-sm text-zinc-400 hover:text-cyan-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h3 className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
              Contact
            </h3>
            <ul className="space-y-4 text-sm text-zinc-400">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>{CONTACT.address}</span>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="footer-email-link"
                  className="flex gap-3 hover:text-cyan-400 transition-colors"
                >
                  <Mail className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`}
                  data-testid="footer-phone-link"
                  className="flex gap-3 hover:text-cyan-400 transition-colors"
                >
                  <Phone className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  {CONTACT.phone}
                </a>
              </li>
            </ul>
            <div className="flex flex-wrap gap-3 mt-8">
              {CONTACT.socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 border border-zinc-800 px-3 py-2 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:border-cyan-400 hover:text-cyan-400 transition-colors"
                >
                  {s.label}
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden select-none" aria-hidden="true">
        <div className="font-display font-extrabold uppercase text-stroke whitespace-nowrap text-center leading-none text-[16vw] -mb-[3vw]">
          Market Rounds
        </div>
      </div>

      <div className="border-t border-zinc-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            © 2026 | Market Rounds Technologies
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/cookie-policy"
              data-testid="footer-cookie-policy-link"
              className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-600 hover:text-cyan-400 transition-colors"
            >
              Cookie Policy
            </Link>
            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-policy-link"
              className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-600 hover:text-cyan-400 transition-colors"
            >
              Privacy Policy
            </Link>
            <button
              onClick={() => window.dispatchEvent(new Event("mr-open-cookie-prefs"))}
              data-testid="footer-cookie-settings-button"
              className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-600 hover:text-cyan-400 transition-colors"
            >
              Cookie Settings
            </button>
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              Nottingham, UK
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
